import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useGlobalCore } from '../../../core/modules/global';
import { UPDATE_COMPANY_DIALOG } from '../../../core/constants/dialog';
import Spinner from '../Spinner';
import Swal from 'sweetalert2';
import { useCompanyCore } from '../../../core/modules/company';
import { useMeCore } from '../../../core/modules/me';
import { PutCompanyPayloadRequest } from '../../../core/modules/company/interfaces/put-company';

const schema = yup.object().shape({
  name: yup.string().required('Campo requerido'),
});

const UpdateDialog = () => {
  const { user } = useMeCore();
  const { dialogs, closeDialog } = useGlobalCore();
  const { company, getCompanies, putCompany, deleteCompany, clearCompany } = useCompanyCore();

  const [loading, setLoading]  = useState(false);

  const canWrite = useMemo(() => user && (user.privileges.superAdmin), [user]);

  const { control, formState, handleSubmit, reset, setValue } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  useEffect(() => {
    if (company) {
      setValue('name', company.name);
    }
  }, [setValue, company]);

  const resetForm = useCallback(() => {
    reset({ name: '' });
    clearCompany();
    closeDialog(UPDATE_COMPANY_DIALOG);
  }, [clearCompany, closeDialog, reset]);

  const onSubmit = useCallback(
    (values: PutCompanyPayloadRequest) => {
      setLoading(true);
      putCompany(company?.id!, values, () => {
        resetForm();
        getCompanies({});
        setLoading(false);
      }, () => {
        setLoading(false);
      });
    },
    [company?.id, getCompanies, putCompany, resetForm]
  );

  const onDelete = useCallback(() => {
    Swal.fire({
      title: "Confimar acción",
      text: `Eliminar la compañia ${company?.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, eliminar!",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);

        deleteCompany(
          company?.id!,
          () => {
            setLoading(false);
            getCompanies({});
            resetForm();
          },
          () => {
            setLoading(false);
          }
        );
      }
    });
  }, [company?.id, company?.name, deleteCompany, getCompanies, resetForm]);

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && Array.isArray(dialogs) && dialogs.includes(UPDATE_COMPANY_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Modificar Compañía
              </Typography>
            </div>
      
            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-24">
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nombre"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.name}
                          helperText={errors?.name?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  {canWrite && (
                    <div className="flex justify-center gap-4">
                      <Button
                        variant="contained"
                        color="error"
                        sx={{ borderRadius: 50 }}
                        disabled={loading}
                        type="button"
                        onClick={onDelete}
                      >
                        <Spinner show={loading} />
                        Eliminar
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        sx={{ borderRadius: 50 }}
                        disabled={!isEmpty(errors) || !isValid || loading}
                        type="submit"
                      >
                        <Spinner show={loading} />
                        Guardar
                      </Button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateDialog;
